// Dashboard — the landing page. A live snapshot of the network: next-7-day
// forecast + loads, trailing accuracy, active/upcoming hub events, capacity
// at-risk hubs, and the top hubs by volume. Everything is computed from the
// forecast engine (no new tables); it reads the same data the Forecast/Network
// pages do.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { Card } from "../components/ui";
import { getErrorMessage } from "../lib/errors";
import { addDays, fmtDate, fmtN, today } from "../lib/format";
import type { Branch, Customer, CustomerLane, Hub, HubEvent } from "../lib/types";
import {
  forecastCustomerHub,
  forecastHubDay,
  impliedLoads,
  meanAbsPctError,
  reroutedInToHub,
  reroutedOutOfHub,
} from "../lib/forecast/engine";
import { flowShares, shareOf } from "../lib/forecast/flowShares";
import { makeTransitFn, receivedInByDay } from "../lib/forecast/transit";
import { useTransitAware } from "../lib/forecast/useTransitAware";
import { getFlowShareTotals, type BranchFlow } from "../features/actuals/branchFlowApi";
import { listCustomers } from "../features/customers/customersApi";
import { listAccountLanes } from "../features/customers/lanesApi";
import { listBranches } from "../features/branches/branchesApi";
import { listHubs } from "../features/hubs/hubsApi";
import {
  buildOverrideMap,
  listAccountOverrides,
  type ForecastOverride,
} from "../features/forecast/overridesApi";
import { listHubEvents } from "../features/hubevents/hubEventsApi";
import {
  hubTotalsFromCustomerActuals,
  listAccountCustomerActuals,
  type CustomerActual,
} from "../features/actuals/customerActualsApi";

const FORECAST_DAYS = 7;
const ACCURACY_DAYS = 28;

/** One headline metric. */
function Kpi({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Card>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-slate-900">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-slate-500">{sub}</p>}
    </Card>
  );
}

function eventStatus(ev: HubEvent, todayStr: string): "active" | "upcoming" {
  return ev.startDate <= todayStr ? "active" : "upcoming";
}

export default function Dashboard() {
  const { session, activeAccount } = useAuth();
  const accountId = activeAccount?.accountId ?? null;

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [hubs, setHubs] = useState<Hub[]>([]);
  const [lanes, setLanes] = useState<Array<{ customerId: string; lane: CustomerLane }>>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [shareFlow, setShareFlow] = useState<BranchFlow[]>([]);
  const [overrides, setOverrides] = useState<ForecastOverride[]>([]);
  const [events, setEvents] = useState<HubEvent[]>([]);
  const [actuals, setActuals] = useState<CustomerActual[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [start] = useState(today());
  const [transitAware] = useTransitAware();

  useEffect(() => {
    if (!accountId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all([
      listCustomers(accountId),
      listHubs(accountId),
      listAccountLanes(accountId),
      listAccountOverrides(accountId),
      listHubEvents(accountId),
      listAccountCustomerActuals(accountId, addDays(start, -ACCURACY_DAYS), addDays(start, -1)),
      listBranches(accountId),
      getFlowShareTotals(accountId, addDays(start, -28), addDays(start, -1)),
    ])
      .then(([c, h, ln, ov, ev, ac, b, sf]) => {
        setCustomers(c);
        setHubs(h.filter((x) => x.active));
        setLanes(ln);
        setOverrides(ov);
        setEvents(ev);
        setActuals(ac);
        setBranches(b.filter((x) => x.active));
        setShareFlow(sf);
      })
      .catch((e) => setError(getErrorMessage(e, "Failed to load dashboard data.")))
      .finally(() => setLoading(false));
  }, [accountId, start]);

  const overrideMap = useMemo(() => buildOverrideMap(overrides), [overrides]);
  const capByKey = useMemo(() => {
    const m = new Map<string, number | null | undefined>();
    for (const { customerId, lane } of lanes) m.set(`${customerId}|${lane.hubId}`, lane.loadCapacity);
    return m;
  }, [lanes]);
  const days = useMemo(
    () => Array.from({ length: FORECAST_DAYS }, (_, i) => addDays(start, i)),
    [start]
  );

  // Per-hub, per-day H2H/Direct received-in → so capacity (peak day) is on the
  // hub's SORTATION load (own + received-in), not just its own volume.
  const shares = useMemo(() => flowShares(shareFlow), [shareFlow]);
  const receivedByHubDay = useMemo(() => {
    if (branches.length === 0) return new Map<string, number>();
    const allHubIds = hubs.map((h) => h.id);
    const tFn = makeTransitFn(hubs, branches, transitAware);
    const cache = new Map<string, number>();
    const ownDay = (o: string, d: string) => {
      const k = `${o}|${d}`;
      let v = cache.get(k);
      if (v === undefined) {
        v = customers.reduce((s, c) => s + forecastCustomerHub(c, o, d, overrideMap, events), 0);
        cache.set(k, v);
      }
      return v;
    };
    return receivedInByDay({ hubIds: allHubIds, branches, dates: days, own: ownDay, share: (o, b) => shareOf(shares, o, b), transit: (o, b) => tFn(o, b) });
  }, [branches, hubs, customers, days, overrideMap, events, shares, transitAware]);

  // Per-hub next-7-day volume + loads + peak SORTATION day + reroute net.
  const perHub = useMemo(() => {
    return hubs
      .map((h) => {
        let volume = 0;
        let loads = 0;
        let peakDay = 0;
        let rerouteIn = 0;
        let rerouteOut = 0;
        for (const d of days) {
          let dayVol = 0;
          for (const c of customers) {
            const v = forecastCustomerHub(c, h.id, d, overrideMap, events);
            if (v > 0) {
              dayVol += v;
              loads += impliedLoads(v, capByKey.get(`${c.id}|${h.id}`));
            }
          }
          volume += dayVol;
          const sortDay = dayVol + (receivedByHubDay.get(`${h.id}|${d}`) ?? 0); // sortation = own + received-in
          if (sortDay > peakDay) peakDay = sortDay;
          if (events.length) {
            rerouteIn += reroutedInToHub(customers, h.id, d, events);
            rerouteOut += reroutedOutOfHub(customers, h.id, d, events);
          }
        }
        return { hub: h, volume, loads, peakDay, rerouteIn, rerouteOut };
      })
      .sort((a, b) => b.volume - a.volume);
  }, [hubs, customers, days, capByKey, overrideMap, events, receivedByHubDay]);

  const networkVolume = useMemo(() => perHub.reduce((s, r) => s + r.volume, 0), [perHub]);
  const networkLoads = useMemo(() => perHub.reduce((s, r) => s + r.loads, 0), [perHub]);
  const anyReroute = useMemo(
    () => perHub.some((r) => r.rerouteIn > 0 || r.rerouteOut > 0),
    [perHub]
  );

  // Capacity-at-risk: hubs with a daily capacity whose peak next-7-day forecast exceeds it.
  const atRisk = useMemo(
    () => perHub.filter((r) => r.hub.capacity != null && r.peakDay > (r.hub.capacity ?? 0)),
    [perHub]
  );

  // Trailing accuracy: backtest the current config against recorded actuals. A
  // hub's actual is the sum of its customers' actuals for that day.
  const accuracy = useMemo(() => {
    if (actuals.length === 0) return null;
    const hubTotals = hubTotalsFromCustomerActuals(actuals); // `${hubId}|${date}` -> total
    const rows = [...hubTotals].map(([key, actual]) => {
      const sep = key.lastIndexOf("|");
      const hid = key.slice(0, sep);
      const date = key.slice(sep + 1);
      return { forecast: forecastHubDay(customers, hid, date, overrideMap, events), actual };
    });
    const mape = meanAbsPctError(rows);
    if (mape == null) return null;
    return { mape, accuracy: Math.max(0, 100 - mape), days: new Set(actuals.map((a) => a.date)).size };
  }, [actuals, customers, overrideMap, events]);

  // Active + upcoming events (hide past ones), soonest first.
  const liveEvents = useMemo(
    () =>
      events
        .filter((e) => e.active !== false && e.endDate >= start)
        .sort((a, b) => (a.startDate < b.startDate ? -1 : 1)),
    [events, start]
  );

  const hubName = useMemo(() => {
    const m = new Map(hubs.map((h) => [h.id, h.name]));
    return (id: string) => m.get(id) ?? "?";
  }, [hubs]);

  if (!accountId) {
    return (
      <Card>
        <p className="text-sm text-slate-500">No active account. Try signing out and back in.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold tracking-tight text-slate-900">Dashboard</h1>
        <p className="text-sm text-slate-500">
          {activeAccount?.name} · {session?.user?.email}
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <Card>
          <p className="text-sm text-slate-500">Loading…</p>
        </Card>
      ) : (
        <>
          {/* Headline KPIs */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            <Kpi label="Active hubs" value={fmtN(hubs.length)} />
            <Kpi
              label="Active customers"
              value={fmtN(customers.filter((c) => c.active).length)}
              sub={`${customers.length} total`}
            />
            <Kpi
              label={`Forecast · next ${FORECAST_DAYS}d`}
              value={fmtN(networkVolume)}
              sub="packages"
            />
            <Kpi label={`Loads · next ${FORECAST_DAYS}d`} value={fmtN(networkLoads)} sub="implied" />
            <Kpi
              label={`Accuracy · last ${ACCURACY_DAYS}d`}
              value={accuracy ? `${accuracy.accuracy.toFixed(1)}%` : "—"}
              sub={accuracy ? `${accuracy.days} days w/ actuals` : "no actuals yet"}
            />
            <Kpi
              label="Active / upcoming events"
              value={fmtN(liveEvents.length)}
              sub={atRisk.length ? `${atRisk.length} hub(s) over capacity` : "no capacity flags"}
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            {/* Top hubs */}
            <Card className="p-0">
              <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
                <span className="text-sm font-medium text-slate-700">
                  Top hubs · next {FORECAST_DAYS} days
                </span>
                <Link
                  to="/year-overview"
                  className="text-xs font-medium text-brand-600 hover:text-brand-700"
                >
                  Year overview →
                </Link>
              </div>
              {perHub.length === 0 ? (
                <p className="p-5 text-sm text-slate-500">No active hubs.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                      <th className="px-5 py-2 font-medium">Hub</th>
                      <th className="px-5 py-2 text-right font-medium">Forecast</th>
                      <th className="px-5 py-2 text-right font-medium">Loads</th>
                      {anyReroute && (
                        <th className="px-5 py-2 text-right font-medium">Reroute</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {perHub.slice(0, 6).map((r) => {
                      const net = r.rerouteIn - r.rerouteOut;
                      const over = r.hub.capacity != null && r.peakDay > (r.hub.capacity ?? 0);
                      return (
                        <tr key={r.hub.id} className="border-b border-slate-100 last:border-0">
                          <td className="px-5 py-2 font-medium text-slate-900">
                            {r.hub.name}
                            {over && (
                              <span
                                className="ml-2 rounded-full bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-700"
                                title={`Peak sortation day ${fmtN(r.peakDay)} (own + received-in) > capacity ${fmtN(r.hub.capacity ?? 0)}`}
                              >
                                over cap
                              </span>
                            )}
                          </td>
                          <td className="px-5 py-2 text-right text-slate-700">{fmtN(r.volume)}</td>
                          <td className="px-5 py-2 text-right text-slate-700">{fmtN(r.loads)}</td>
                          {anyReroute && (
                            <td
                              className={`px-5 py-2 text-right ${
                                net > 0
                                  ? "text-emerald-600"
                                  : net < 0
                                    ? "text-red-600"
                                    : "text-slate-400"
                              }`}
                              title={`${fmtN(r.rerouteIn)} in · ${fmtN(r.rerouteOut)} out`}
                            >
                              {net === 0 ? "—" : `${net > 0 ? "+" : "−"}${fmtN(Math.abs(net))}`}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </Card>

            {/* Active / upcoming events */}
            <Card className="p-0">
              <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
                <span className="text-sm font-medium text-slate-700">Active &amp; upcoming events</span>
                <Link to="/events" className="text-xs font-medium text-brand-600 hover:text-brand-700">
                  Hub Events →
                </Link>
              </div>
              {liveEvents.length === 0 ? (
                <p className="p-5 text-sm text-slate-500">
                  No active or upcoming hub events. Closures, capacity changes, and reroutes show here.
                </p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                      <th className="px-5 py-2 font-medium">Hub</th>
                      <th className="px-5 py-2 font-medium">Type</th>
                      <th className="px-5 py-2 font-medium">Window</th>
                      <th className="px-5 py-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {liveEvents.slice(0, 8).map((ev) => {
                      const status = eventStatus(ev, start);
                      return (
                        <tr key={ev.id} className="border-b border-slate-100 last:border-0">
                          <td className="px-5 py-2 font-medium text-slate-900">
                            {hubName(ev.hubId)}
                            {ev.mode === "by_customer" && (
                              <span className="ml-1 text-xs text-slate-400">(by customer)</span>
                            )}
                          </td>
                          <td className="px-5 py-2 capitalize text-slate-600">{ev.type}</td>
                          <td className="px-5 py-2 text-slate-600">
                            {fmtDate(ev.startDate)} → {fmtDate(ev.endDate)}
                          </td>
                          <td className="px-5 py-2">
                            <span
                              className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                                status === "active"
                                  ? "bg-emerald-50 text-emerald-700"
                                  : "bg-slate-100 text-slate-500"
                              }`}
                            >
                              {status}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </Card>
          </div>

          {customers.length === 0 && (
            <Card>
              <p className="text-sm text-slate-600">
                No customers yet.{" "}
                <Link to="/customers" className="font-medium text-brand-600 hover:text-brand-700">
                  Add a customer
                </Link>{" "}
                to start forecasting.
              </p>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
